import { useEffect, useRef, useState } from "react";
import { token, domain } from "../../../../../components/Security";
import axios from "../../../../../api/apiClient";

import CloseIcon from "@mui/icons-material/Close";
import { SetAuditTrail } from "../../../../../components/Reports/AuditTrailReportEmployeeSetup";
import PaytypePickerField from "../../../../../common/selectComponents/PaytypeTypePicker";
import PayrollPeriodPickerField from "../../../../../common/selectComponents/PayrollPeriodPicker";
import ClassificationPickerField from "../../../../../common/selectComponents/ClassificationPicker";

export function RevertPayrollForm({
	isOpen,
	onClose,
	progress,
	simulateLoading,
	setLoadingRequest,
	setLoadingPercentage,
	fetchOtherAddition,
	fetchOtherDeduction,
	fetchOtherAdjustment,
	fetchLateAdjustmentData,
	fetchProcess,
	showGeneralMessage,
	revertPayroll,
	setIsRevert
}) {

	const [errorState, setErrorState] = useState(false);
	const [payrollPeriodId, setPayrollPeriodId] = useState("");
	const [payrollPeriodName, setPayrollPeriodName] = useState(""); // display purpose
	const [payTypeId, setPayTypeId] = useState("");
	const [payTypeName, setPayTypeName] = useState(""); // display purpose
	const [classificationId, setClassificationId] = useState("");
	const [classificationDescription, setClassificationDescription] = useState("");  // display purpose

	const submit = (e) => {
		if (!payrollPeriodId || !payTypeId || !classificationId) {
			setErrorState(true);
			return;
		}
		handleClose();
		handleRevert(e);
		setErrorState(false);
		setIsRevert(true);
	};

	const handleClose = () => {
		onClose();
		setPayrollPeriodId("");
		setPayTypeId("");
		setClassificationId("");
		setErrorState(false);
	};
	
	const handleRevert = async (event) => {
		event.preventDefault();

		try {
			setLoadingPercentage(10); // Initial 10%
			setLoadingRequest(true);

			// Simulate gradual loading progress while waiting for axios.post
			simulateLoading(30, 200); // Simulate increase to 30% slowly over 200ms per step

			const revertDataParam = `${payTypeId}/${payrollPeriodId}/${classificationId}`;
			
			await revertPayroll(revertDataParam);

			setLoadingPercentage(30);

			await simulateLoading(60, 100);

			await fetchOtherAddition();

			// Simulate gradual progress to 70%
			await simulateLoading(70, 100);

			await fetchOtherDeduction();

			// Simulate gradual progress to 80%
			await simulateLoading(80, 100);

			await fetchOtherAdjustment();
			await fetchLateAdjustmentData();

			// Simulate gradual progress to 90%
			await simulateLoading(90, 100);

			await fetchProcess();

			showGeneralMessage("Payroll has been reverted!", true);

			// Final smooth increase to 95%
			simulateLoading(95, 100);
			handleClose();
		} catch (error) {
			// Stop any ongoing simulation
			setLoadingPercentage(progress); // Stop at the current percentage
			setLoadingRequest(false); // Set the loading state to false

			console.error("Error:", error);
			
			let errorMessage = "Error processing request!";

			if (error.response) {
				errorMessage = error.response.data?.message || error.response.data || "Server Error: " + error.response.status;
			} else if (error.request) {
				errorMessage = "No response from server. Please check your connection.";
			} else {
				errorMessage = error.message;
			}
			
			showGeneralMessage(errorMessage, false);

			await simulateLoading(0, 100); // Gradually decrease to 0 for a smoother UX
		} finally {
			// Finalize loading process and reset
			await simulateLoading(100, 100); // Gradually increment to 100% before closing
			setTimeout(() => {
				setLoadingPercentage(0);
				setLoadingRequest(false);
			}, 500); // Delay to show full completion before resetting
		}
	};

	const [currentPayTypeId, setCurrentPayTypeId] = useState("");
	useEffect(() => {
		if (currentPayTypeId !== payTypeId) {
			setCurrentPayTypeId(payTypeId);
			setClassificationId("");
			setPayrollPeriodId("");
		}
	}, [currentPayTypeId, payTypeId]);

	if (!isOpen) return;

	return (
		<>
			<div className="fixed inset-0 z-[10000] flex items-center justify-center overflow-auto bg-black/50 p-3"
				style={{ marginTop: "0px" }}>
				<div className="flex w-full flex-col rounded-lg bg-white shadow-xl md:w-[500px]">
					<div className="flex items-center justify-between px-6 py-4 shadow-sm" >
						<h2 className="text-xl font-bold text-customMainColor">Revert Payroll</h2>
						<button onClick={handleClose} className="cursor-pointer border-none bg-transparent"><CloseIcon /></button>
					</div>

					<div className="bg-gray-50 p-6">
						<div className="space-y-4 rounded-lg border border-gray-300 bg-white p-4 shadow-sm">
							<PaytypePickerField
								label="Paytype"
								payTypeId={payTypeId || ""}
								displayValue={payTypeName || "-- Select Paytype --"}
								onSelect={(paytype) => {
									setPayTypeId(paytype.id);
									setPayTypeName(paytype.description);
								}}
								onClear={(paytype) => {
									setPayTypeId("");
									setPayTypeName("");
								}}
								disabled={false}
								allowClear={true}
								isRequired={true} // ADDED: mark as required
								error={!payTypeId && errorState} // ADDED: error prop
								errorMessage={"Please fill up this field"} // ADDED: errorMessage prop
							/>

							<PayrollPeriodPickerField
								label="PayrollPeriod"
								payrollPeriodId={payrollPeriodId || ""}
								payTypeId={payTypeId}
								displayValue={payrollPeriodName || "-- Select Payroll Period --"}
								onSelect={(payrollPeriod) => {
									setPayrollPeriodId(payrollPeriod.id);
									setPayrollPeriodName(payrollPeriod.name);
								}}
								onClear={() => {
									setPayrollPeriodId("");
									setPayrollPeriodName("");
								}}
								disabled={!payTypeId}
								allowClear={true}
								isRequired={true} // ADDED: mark as required
								error={!payrollPeriodId && errorState} // ADDED: error prop
								errorMessage={"Please fill up this field"} // ADDED: errorMessage prop
							/>

							<ClassificationPickerField
								label="Classification"
								classificationId={classificationId || ""}
								displayValue={classificationDescription || "-- Select Classification --"}
								onSelect={(classification) => {
									setClassificationId(classification.id);
									setClassificationDescription(classification.description);
								}}
								onClear={() => {
									setClassificationId("");
									setClassificationDescription("");
								}}
								disabled={!payTypeId}
								allowClear={true}
								isRequired={true} // ADDED: mark as required
								error={!payrollPeriodId && errorState} // ADDED: error prop
								errorMessage={"Please fill up this field"} // ADDED: errorMessage prop
							/>
						</div>
					</div>

					<div className="flex justify-end space-x-2 border-t p-5">
						<button onClick={handleClose} className="cursor-pointer rounded border-none bg-gray-200 px-4 py-2">Cancel</button>
						<button onClick={(e) => submit(e)} className="cursor-pointer rounded border-none bg-blue-600 px-6 py-2 text-white">Revert</button>
					</div>
				</div>
			</div>
		</>
	);
}